import { findBook, findBooks, normalize } from './books'
import { CHAPTERS, bookNames } from './bibles'

const BOOK = String.raw`[1-3]?[\s.]*[a-z][a-z\s.]*?`
const VERSES = String.raw`\d+(?:\s*-\s*\d+)?(?:\s*[.,]\s*\d+(?:\s*-\s*\d+)?)*`

const REFERENCE = new RegExp(String.raw`^(${BOOK})[\s.]*(\d+)(?:(?:\s*[,:.]\s*|\s+)(${VERSES}))?$`)
const RELATIVE = new RegExp(String.raw`^(\d+)(?:\s*[,:.]\s*(${VERSES}))?$`)
const BARE = new RegExp(String.raw`^(${BOOK})[\s.]*$`)
const SCOPE = new RegExp(String.raw`^(${BOOK})\s*:\s*(.+)$`)

const parseVerses = verses => verses
  ? verses.split(/\s*[.,]\s*/).map(range => {
    const [from, to = from] = range.split(/\s*-\s*/).map(Number)
    return [Math.min(from, to), Math.max(from, to)]
  })
  : null

const reference = (book, chapter, verses, bare = false) => (
  book && chapter >= 1 && chapter <= CHAPTERS[book]
    ? {book, chapter, verses: parseVerses(verses), bare}
    : null
)

const escape = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Każde słowo musi pasować do początku wyrazu, fraza w cudzysłowie – w całości,
// a słowa poprzedzone minusem wykluczają werset.
const parseTerms = text => {
  const include = []
  const exclude = []

  for (const [, minus, phrase, word] of text.replace(/[„”“«»]/g, '"').matchAll(/(-?)(?:"([^"]+)"?|(\S+))/g)) {
    const words = normalize(phrase ?? word).split(/[^\p{L}\p{N}]+/u).filter(Boolean)

    if (words.length) {
      const pattern = `(?<![\\p{L}\\p{N}])${words.map(escape).join('[^\\p{L}\\p{N}]+')}`
      minus ? exclude.push(pattern) : include.push(pattern)
    }
  }

  return {include, exclude}
}

const ATTEMPTS = [
  [REFERENCE, match => [match[1], +match[2], match[3]]],
  [RELATIVE, match => [null, +match[1], match[2]]],
  [BARE, match => [match[1], 1, null]]
]

// Rozbiera zapytanie na odnośnik (np. „J 3,16”, „1 Kor 13,4-7”, „Ps 23”, „Mt 5,3.9”), podpowiedzi
// pasujących ksiąg (np. „Kor 13” → 1 i 2 Koryntian 13) i wyszukiwanie tekstu (np. „miłość cierpliw”,
// „"na początku"”, „Mt: królestwo”). Sama nazwa księgi jest jednocześnie odnośnikiem i tekstem.
export const parseQuery = (query, currentBook) => {
  const q = normalize(query).replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim()
  const result = {reference: null, suggestions: [], text: null}
  let match

  if (!q) return result

  for (const [pattern, groups] of ATTEMPTS) {
    if (!(match = q.match(pattern))) continue

    const [name, chapter, verses] = groups(match)
    const bare = pattern == BARE
    const exact = name ? findBook(name, {prefix: !bare, bare}) : currentBook
    const books = name ? findBooks(name, {bare}) : [currentBook]
    const suggestions = [...new Set([exact, ...books])].map(book => reference(book, chapter, verses, bare)).filter(Boolean)

    if (suggestions.length) {
      result.reference = reference(exact, chapter, verses, bare)
      result.suggestions = suggestions.slice(0, 5)
      break
    }
  }

  // Odnośnik z numerem rozdziału nie jest wyszukiwaniem tekstu, nawet gdy pasuje do kilku ksiąg.
  if (result.suggestions[0] && !result.suggestions[0].bare) return result

  let book = null
  let text = query

  if (match = q.match(SCOPE)) {
    book = findBook(match[1], {prefix: true})
    book && (text = query.slice(query.indexOf(':') + 1))
  }

  const terms = parseTerms(text)

  result.text = terms.include.length ? {book, ...terms} : null

  return result
}

// Zapis odnośnika w języku przekładu, np. „Jana 3:16-18,20”, „Psalms 23” albo samo „Rodzaju”.
export const formatReference = ({book, chapter, verses, bare}, bible) => (
  bare
    ? bookNames(bible)[book]
    : `${bookNames(bible)[book]} ${chapter}${verses ? `:${verses.map(([from, to]) => from == to ? from : `${from}-${to}`).join(',')}` : ''}`
)

// Zwraca funkcję sprawdzającą, czy znormalizowany tekst wersetu pasuje do zapytania.
export const matcher = ({include, exclude}) => {
  const compile = patterns => patterns.map(pattern => new RegExp(pattern, 'u'))
  const included = compile(include)
  const excluded = compile(exclude)

  return text => included.every(pattern => pattern.test(text)) && !excluded.some(pattern => pattern.test(text))
}

// Dzieli tekst na fragmenty z zaznaczonymi trafieniami: [[tekst, czyTrafienie], ...].
export const highlight = (text, patterns) => {
  let normalized = ''
  const index = []

  for (let i = 0; i < text.length; i++) {
    const char = normalize(text[i])
    normalized += char
    index.push(...Array(char.length).fill(i))
  }

  index.push(text.length)

  const ranges = patterns.flatMap(pattern => [...normalized.matchAll(new RegExp(pattern, 'gu'))].map(match => (
    [index[match.index], index[match.index + match[0].length]]
  ))).sort((a, b) => a[0] - b[0])

  const parts = []
  let last = 0

  for (const [start, end] of ranges) {
    if (end <= last) continue
    start > last && parts.push([text.slice(last, start), false])
    parts.push([text.slice(Math.max(start, last), end), true])
    last = end
  }

  last < text.length && parts.push([text.slice(last), false])

  return parts
}
